import { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Textarea } from '../components/ui/textarea';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from '../components/ui/dialog';
import { User, Save, Loader2, Download, Trash2, AlertTriangle } from 'lucide-react';
import api from '../lib/api';

export default function ProfilePage() {
  const { user, updateUser, logout } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({
    name: user?.name || '',
    phone: user?.phone || '',
    city: user?.city || '',
    state: user?.state || '',
    bio: user?.bio || '',
  });
  const [saving, setSaving] = useState(false);
  const [success, setSuccess] = useState('');
  const [exporting, setExporting] = useState(false);
  const [deleteDialog, setDeleteDialog] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState('');

  const update = (k, v) => setForm(p => ({ ...p, [k]: v }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccess('');
    try {
      const { data } = await api.put('/auth/profile', form);
      updateUser(data.user);
      setSuccess('Perfil atualizado!');
    } catch { /* ignore */ }
    setSaving(false);
  };

  const handleExport = async () => {
    setExporting(true);
    try {
      const { data } = await api.get('/auth/export-data');
      const blob = new Blob([JSON.stringify(data, null, 2)], { type: 'application/json' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `vdg_meus_dados_${user?.name || 'usuario'}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      alert('Erro ao exportar dados. Tente novamente.');
    }
    setExporting(false);
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      await api.delete('/auth/delete-account');
      logout();
      navigate('/');
    } catch (err) {
      alert(err.response?.data?.detail || 'Erro ao excluir conta. Tente novamente.');
    }
    setDeleting(false);
    setDeleteDialog(false);
  };

  return (
    <div className="max-w-lg mx-auto px-4 sm:px-6 py-8" data-testid="profile-page">
      <h1 className="text-2xl sm:text-3xl font-bold tracking-tight mb-6" style={{ fontFamily: 'Outfit' }}>Meu Perfil</h1>

      <div className="flex items-center gap-4 mb-8 p-4 rounded-xl border border-border bg-card">
        <div className="h-16 w-16 rounded-full bg-primary/10 flex items-center justify-center">
          <User className="h-8 w-8 text-primary" />
        </div>
        <div>
          <p className="font-semibold">{user?.name}</p>
          <p className="text-sm text-muted-foreground">{user?.email}</p>
          {!user?.email_verified && (
            <span className="text-xs text-amber-600 font-medium">Email não verificado</span>
          )}
        </div>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4" data-testid="profile-form">
        {success && <div className="p-3 rounded-lg bg-primary/10 text-primary text-sm">{success}</div>}

        <div className="space-y-2">
          <Label>Nome</Label>
          <Input value={form.name} onChange={e => update('name', e.target.value)} data-testid="profile-name-input" />
        </div>
        <div className="space-y-2">
          <Label>Telefone</Label>
          <Input value={form.phone} onChange={e => update('phone', e.target.value)} placeholder="(11) 99999-9999" data-testid="profile-phone-input" />
        </div>
        <div className="grid grid-cols-2 gap-4">
          <div className="space-y-2">
            <Label>Cidade</Label>
            <Input value={form.city} onChange={e => update('city', e.target.value)} placeholder="São Paulo" data-testid="profile-city-input" />
          </div>
          <div className="space-y-2">
            <Label>Estado</Label>
            <Input value={form.state} onChange={e => update('state', e.target.value)} placeholder="SP" data-testid="profile-state-input" />
          </div>
        </div>
        <div className="space-y-2">
          <Label>Bio</Label>
          <Textarea value={form.bio} onChange={e => update('bio', e.target.value)} placeholder="Conte um pouco sobre você..." rows={3} data-testid="profile-bio-input" />
        </div>
        <Button type="submit" className="w-full rounded-lg gap-2" disabled={saving} data-testid="profile-save-btn">
          {saving ? <Loader2 className="h-4 w-4 animate-spin" /> : <Save className="h-4 w-4" />}
          {saving ? 'Salvando...' : 'Salvar Alterações'}
        </Button>
      </form>

      {/* Data Management Section (LGPD) */}
      <div className="mt-10 pt-8 border-t border-border space-y-5">
        <h2 className="text-lg font-semibold" style={{ fontFamily: 'Outfit' }}>Meus Dados (LGPD)</h2>
        <p className="text-sm text-muted-foreground">Conforme a Lei Geral de Proteção de Dados, você tem direito de exportar ou excluir seus dados pessoais da plataforma.</p>

        {/* Export Data */}
        <div className="p-4 rounded-xl border border-border bg-card space-y-3">
          <div className="flex items-start gap-3">
            <Download className="h-5 w-5 text-primary mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold">Exportar meus dados</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Baixe uma cópia completa dos seus dados pessoais, anúncios, transações, mensagens e avaliações em formato JSON.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={handleExport} disabled={exporting} className="w-full rounded-lg gap-2" data-testid="export-data-btn">
            {exporting ? <Loader2 className="h-4 w-4 animate-spin" /> : <Download className="h-4 w-4" />}
            {exporting ? 'Exportando...' : 'Exportar Dados'}
          </Button>
        </div>

        {/* Delete Account */}
        <div className="p-4 rounded-xl border border-destructive/30 bg-destructive/5 space-y-3">
          <div className="flex items-start gap-3">
            <AlertTriangle className="h-5 w-5 text-destructive mt-0.5 shrink-0" />
            <div className="flex-1">
              <h3 className="text-sm font-semibold text-destructive">Excluir minha conta</h3>
              <p className="text-xs text-muted-foreground mt-0.5">Esta ação é irreversível. Todos os seus dados pessoais, anúncios e informações serão permanentemente removidos da plataforma.</p>
            </div>
          </div>
          <Button variant="outline" size="sm" onClick={() => setDeleteDialog(true)} className="w-full rounded-lg gap-2 text-destructive border-destructive/30 hover:bg-destructive/10" data-testid="delete-account-btn">
            <Trash2 className="h-4 w-4" /> Excluir Minha Conta
          </Button>
        </div>
      </div>

      {/* Delete Account Confirmation Dialog */}
      <Dialog open={deleteDialog} onOpenChange={v => { setDeleteDialog(v); setDeleteConfirm(''); }}>
        <DialogContent data-testid="delete-account-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2 text-destructive">
              <AlertTriangle className="h-5 w-5" /> Excluir Conta
            </DialogTitle>
            <DialogDescription>
              Esta ação é <strong>irreversível</strong>. Todos os seus dados serão permanentemente removidos, incluindo anúncios, transações e mensagens. Recomendamos exportar seus dados antes de prosseguir.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-2 my-2">
            <Label className="text-xs text-muted-foreground">
              Digite <span className="font-mono font-bold text-foreground">EXCLUIR</span> para confirmar:
            </Label>
            <Input
              value={deleteConfirm}
              onChange={e => setDeleteConfirm(e.target.value)}
              placeholder="EXCLUIR"
              data-testid="delete-confirm-input"
            />
          </div>
          <DialogFooter className="gap-2 sm:gap-0">
            <Button variant="outline" onClick={() => { setDeleteDialog(false); setDeleteConfirm(''); }} disabled={deleting} data-testid="cancel-delete-account-btn">
              Cancelar
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteAccount}
              disabled={deleting || deleteConfirm !== 'EXCLUIR'}
              className="gap-2"
              data-testid="confirm-delete-account-btn"
            >
              <Trash2 className="h-4 w-4" /> {deleting ? 'Excluindo...' : 'Excluir Permanentemente'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
